import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

const CHARSETS: Record<string, string> = {
    'alphanumeric': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
    'alpha': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
    'lowercase': 'abcdefghijklmnopqrstuvwxyz',
    'uppercase': 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    'numeric': '0123456789',
    'hex-lower': '0123456789abcdef',
    'hex-upper': '0123456789ABCDEF',
    'base64-url': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_',
    'symbols': '!@#$%^&*()-_=+[]{}<>,.?/:;',
    'custom': '',
};

interface State {
    charset: string;
    customChars: string;
    length: number;
    count: number;
    output: string;
    copied: boolean;
}

function randomString(chars: string, len: number): string {
    if (!chars) return '';
    const buf = new Uint32Array(len);
    crypto.getRandomValues(buf);
    let out = '';
    for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
    return out;
}

export default class RandomStringComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        charset: 'alphanumeric',
        customChars: 'abc123',
        length: 16,
        count: 10,
        output: '',
        copied: false,
    };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = () => {
        const chars = this.state.charset === 'custom' ? this.state.customChars : CHARSETS[this.state.charset];
        const out: string[] = [];
        for (let i = 0; i < this.state.count; i++) out.push(randomString(chars, this.state.length));
        this.setState({ output: out.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { charset, customChars, length, count, output, copied } = this.state;
        const stringCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.field}>
                    <label htmlFor="rs-charset">Charset</label>
                    <select id="rs-charset" value={charset}
                        onChange={e => this.setState({ charset: e.target.value }, this.generate)}>
                        {Object.keys(CHARSETS).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                </div>
                {charset === 'custom' && (
                    <div className={genStyles.field}>
                        <label htmlFor="rs-custom">Custom chars</label>
                        <input id="rs-custom" type="text" value={customChars}
                            onChange={e => this.setState({ customChars: e.target.value }, this.generate)} />
                    </div>
                )}
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="rs-length">Length</label>
                        <input id="rs-length" type="number" inputMode="decimal" min={1} max={1024} value={length}
                            onChange={e => this.setState({ length: Math.max(1, Math.min(1024, +e.target.value || 16)) }, this.generate)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="rs-count"># of strings</label>
                        <input id="rs-count" type="number" inputMode="decimal" min={1} max={1000} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(1000, +e.target.value || 10)) }, this.generate)} />
                    </div>
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                    ↻ Regenerate
                </button>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated strings"
                previewMeta={`${stringCount} string${stringCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated random strings">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
            />
        );
    }
}
